/**
 * Password validation utilities matching backend requirements
 * Backend: internal/util/password/validator.go
 */

const COMMON_PASSWORDS = new Set([
	"password123",
	"admin123",
	"qwerty123",
	"letmein",
	"welcome123",
	"password123!",
	"welcome123!",
	"qwerty123456",
	"password1234",
	"admin1234567",
	"letmein123456",
	"qwertyuiop123",
	"123456789012",
]);

export interface PasswordValidationResult {
	valid: boolean;
	errors: string[];
}

/**
 * Validate password against backend requirements
 */
export function validatePassword(
	password: string,
	email?: string,
): PasswordValidationResult {
	const errors: string[] = [];

	// Length check
	if (password.length < 12) {
		errors.push("Password must be at least 12 characters");
	}
	if (password.length > 128) {
		errors.push("Password must be less than 128 characters");
	}

	// Character requirements
	if (!/[A-Z]/.test(password)) {
		errors.push("Password must contain at least one uppercase letter");
	}
	if (!/[a-z]/.test(password)) {
		errors.push("Password must contain at least one lowercase letter");
	}
	if (!/[0-9]/.test(password)) {
		errors.push("Password must contain at least one digit");
	}
	if (!/[^A-Za-z0-9]/.test(password)) {
		errors.push("Password must contain at least one special character");
	}

	// Common password check
	if (COMMON_PASSWORDS.has(password.toLowerCase())) {
		errors.push("Password is too common");
	}

	// Email username check
	if (email) {
		const username = email.split("@")[0];
		if (
			username.length > 3 &&
			password.toLowerCase().includes(username.toLowerCase())
		) {
			errors.push("Password cannot contain your email username");
		}
	}

	return {
		valid: errors.length === 0,
		errors,
	};
}

/**
 * Get password strength indicator
 */
export function getPasswordStrength(password: string): {
	score: number;
	label: string;
	color: string;
} {
	let score = 0;

	// Length bonus
	if (password.length >= 12) score += 1;
	if (password.length >= 16) score += 1;
	if (password.length >= 20) score += 1;

	// Character variety
	if (/[A-Z]/.test(password)) score += 1;
	if (/[a-z]/.test(password)) score += 1;
	if (/[0-9]/.test(password)) score += 1;
	if (/[^A-Za-z0-9]/.test(password)) score += 1;

	// Penalties
	if (COMMON_PASSWORDS.has(password.toLowerCase())) score = 0;

	if (score <= 2) {
		return { score, label: "Weak", color: "text-destructive" };
	}
	if (score <= 4) {
		return { score, label: "Fair", color: "text-amber-600" };
	}
	if (score <= 6) {
		return { score, label: "Good", color: "text-blue-600" };
	}
	return { score, label: "Strong", color: "text-emerald-600" };
}
